import axios, { type AxiosRequestHeaders } from 'axios';
import {
  getStoredAuthToken,
  hasStoredOwnerAdminSession,
  shouldUseDevOwnerAuth,
} from '../utils/authStorage';

// Keep API base consistent with auth.ts so all services
// talk to the same backend origin and `/api` prefix.
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';
const API_URL = `${API_BASE}/api`;

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }
    (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${token}`;
  }
  if (hasStoredOwnerAdminSession() || shouldUseDevOwnerAuth()) {
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }
    (config.headers as AxiosRequestHeaders)['X-Tellurvision-Dev-Owner'] = 'true';
  }
  return config;
});

// Function to share an ad on social media
function extractErrorMessage(err: unknown): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null && 'message' in err)
    return String((err as { message?: unknown }).message ?? '');
  return String(err);
}

export const shareAd = async (adId: string, platform: string) => {
  try {
    const response = await apiClient.post(`/ads/${adId}/share`, { platform });
    return response.data;
  } catch (error) {
    const msg = extractErrorMessage(error);
    throw new Error('Error sharing ad: ' + msg);
  }
};

// Function to process a payment
export const processPayment = async (paymentData: Record<string, unknown>) => {
  try {
    const response = await apiClient.post('/payments', paymentData);
    return response.data;
  } catch (error) {
    const msg = extractErrorMessage(error);
    throw new Error('Error processing payment: ' + msg);
  }
};

// Function to get user payment history
export const getPaymentHistory = async (userId: string) => {
  try {
    const response = await apiClient.get(`/payments/history/${userId}`);
    return response.data;
  } catch (error) {
    const msg = extractErrorMessage(error);
    throw new Error('Error fetching payment history: ' + msg);
  }
};

// Function to get admin dashboard data
export const getAdminDashboardData = async () => {
  try {
    const response = await apiClient.get('/admin/dashboard');
    return response.data;
  } catch (error) {
    const msg = extractErrorMessage(error);
    throw new Error('Error fetching admin dashboard data: ' + msg);
  }
};

export interface DailyAdStat {
  date: string; // YYYY-MM-DD
  views: number;
  clicks: number;
  telemetryBlankEvents: number;
}

interface DailyAdStatsOptions {
  adId?: string;
  userId?: string;
  from?: string;
  to?: string;
}

export const getDailyAdStats = async (options: DailyAdStatsOptions): Promise<DailyAdStat[]> => {
  try {
    const response = await apiClient.get('/ads/stats/daily', { params: options });
    const body = response.data as { items?: DailyAdStat[] } | DailyAdStat[];
    if (Array.isArray(body)) {
      return body as DailyAdStat[];
    }
    return Array.isArray(body.items) ? (body.items as DailyAdStat[]) : [];
  } catch (error) {
    const msg = extractErrorMessage(error);
    throw new Error('Error fetching daily ad stats: ' + msg);
  }
};
